/**
 * 统一音频控制器类
 * 用于简化对不同平台音频播放器的控制
 * 提供统一的API接口，内部自动处理不同播放器的差异
 */

import type { RoomState, RootState } from "../state";

/**
 * 统一音频控制器接口
 */
export interface IAudioController {
  /**
   * 停止当前播放并清空队列
   */
  stop(): void;

  /**
   * 暂停当前播放
   */
  pause(): void;

  /**
   * 恢复播放
   */
  resume(): void;

  /**
   * 清空播放队列
   */
  clearQueue(): void;

  /**
   * 获取当前播放状态
   */
  isPlaying(): boolean;

  /**
   * 设置产品切换相关状态
   * @param productId 目标产品ID
   */
  setProductSwitch(productId: string | null): void;

  /**
   * 清空内部去重集合（如addedIds）
   */
  clearInternalCollections(): void;
}

/**
 * 统一音频控制器实现类
 * 自动检测并控制不同平台的音频播放器
 */
export class AudioController implements IAudioController {
  private state: RoomState & { [key: string]: any };
  private roomId: string;

  /**
   * 构造函数
   * @param state 当前房间状态
   * @param roomId 房间ID
   */
  constructor(state: RoomState & { [key: string]: any }, roomId: string) {
    this.state = state;
    this.roomId = roomId;
  }

  /**
   * 停止当前播放并清空队列
   */
  stop(): void {
    try {
      // 清空队列
      this.state.queue = [];

      // 停止iOS WebAudio播放器（如果存在）
      if (this.state.__appleWA__) {
        if (this.state.__appleWA__.player) {
          this.state.__appleWA__.player.stop();
        }
        // 清理产品切换相关状态
        this.state.__appleWA__.preparingTargetPid = null;
        this.state.__appleWA__.targetPrepared = false;
        // 清空去重集合 addedIds
        if (this.state.__appleWA__.addedIds) {
          this.state.__appleWA__.addedIds.clear();
          console.log(`[AudioController] iOS播放器addedIds已清空`);
        }
        // 清空ID版本映射
        if (this.state.__appleWA__.idVersionMap) {
          this.state.__appleWA__.idVersionMap.clear();
          console.log(`[AudioController] iOS播放器idVersionMap已清空`);
        }
        console.log(`[AudioController] iOS播放器已停止并清空队列`);
      }

      // 停止Universal WebAudio播放器（如果存在）
      if ((this.state as any).universalWebAudioPlayer) {
        (this.state as any).universalWebAudioPlayer.stop();
        // 清空Universal播放器的addedIds（如果存在）
        if ((this.state as any).universalWebAudioPlayer.addedIds) {
          (this.state as any).universalWebAudioPlayer.addedIds.clear();
          console.log(`[AudioController] Universal播放器addedIds已清空`);
        }
        this.state?.destroyUniversalWebAudio();
        console.log(`[AudioController] Universal播放器已停止并清空队列`);
      }

      // 停止Howler播放器（如果存在）
      if (this.state.__howlerPlayer) {
        this.state.__howlerPlayer.stop();
        console.log(`[AudioController] Howler播放器已停止`);
      }

      // 更新播放状态
      this.state.isPlaying = false;
      this.state.currentIndex = 0;

      // 清理产品切换相关状态
      this.state.switchingProductId = null;
      this.state.pendingProductSwitch = null;

      // 清理高性能队列相关数据
      if (this.state.highPerformanceQueue) {
        // 清空高性能队列（如果有clear方法）
        if (typeof this.state.highPerformanceQueue.clear === "function") {
          this.state.highPerformanceQueue.clear();
          console.log(`[AudioController] 高性能队列已清空`);
        }
      }

      console.log(
        `[AudioController] 所有播放器已停止，队列及相关数据结构已清空`
      );
    } catch (error) {
      console.error(`[AudioController] 停止播放器时出错:`, error);
    }
  }

  /**
   * 暂停当前播放
   */
  pause(): void {
    try {
      // 暂停iOS WebAudio播放器
      if (this.state.__appleWA__ && this.state.__appleWA__.player) {
        this.state.__appleWA__.player.pause();
        console.log(`[AudioController] iOS播放器已暂停`);
      }

      // 暂停Universal WebAudio播放器
      if (
        (this.state as any).universalWebAudioPlayer &&
        typeof (this.state as any).universalWebAudioPlayer.pause === "function"
      ) {
        (this.state as any).universalWebAudioPlayer.pause();
        console.log(`[AudioController] Universal播放器已暂停`);
      }

      // 暂停Howler播放器
      if (this.state.__howlerPlayer) {
        this.state.__howlerPlayer.pause();
        console.log(`[AudioController] Howler播放器已暂停`);
      }

      // 更新播放状态
      this.state.isPlaying = false;
      console.log(`[AudioController] 所有播放器已暂停`);
    } catch (error) {
      console.error(`[AudioController] 暂停播放器时出错:`, error);
    }
  }

  /**
   * 恢复播放
   */
  resume(): void {
    try {
      // 恢复iOS WebAudio播放器
      if (
        this.state.__appleWA__ &&
        this.state.__appleWA__.player &&
        typeof this.state.__appleWA__.player.resume === "function"
      ) {
        this.state.__appleWA__.player.resume();
        console.log(`[AudioController] iOS播放器已恢复播放`);
      }

      // 恢复Universal WebAudio播放器
      if (
        (this.state as any).universalWebAudioPlayer &&
        typeof (this.state as any).universalWebAudioPlayer.resume === "function"
      ) {
        (this.state as any).universalWebAudioPlayer.resume();
        console.log(`[AudioController] Universal播放器已恢复播放`);
      }

      // 恢复Howler播放器
      if (this.state.__howlerPlayer) {
        this.state.__howlerPlayer.play();
        console.log(`[AudioController] Howler播放器已恢复播放`);
      }

      // 更新播放状态
      this.state.isPlaying = true;
      console.log(`[AudioController] 所有播放器已恢复播放`);
    } catch (error) {
      console.error(`[AudioController] 恢复播放时出错:`, error);
    }
  }

  /**
   * 清空播放队列
   */
  clearQueue(): void {
    try {
      // 清空队列
      this.state.queue = [];

      // 清空iOS WebAudio播放器队列
      if (this.state.__appleWA__) {
        if (
          this.state.__appleWA__.player &&
          typeof this.state.__appleWA__.player.clearQueue === "function"
        ) {
          this.state.__appleWA__.player.clearQueue();
          console.log(`[AudioController] iOS播放器队列已清空`);
        }
        // 清空去重集合 addedIds
        if (this.state.__appleWA__.addedIds) {
          this.state.__appleWA__.addedIds.clear();
          console.log(`[AudioController] iOS播放器addedIds已清空`);
        }
        // 清空ID版本映射
        if (this.state.__appleWA__.idVersionMap) {
          this.state.__appleWA__.idVersionMap.clear();
          console.log(`[AudioController] iOS播放器idVersionMap已清空`);
        }
      }

      // 清空Universal WebAudio播放器队列
      if ((this.state as any).universalWebAudioPlayer) {
        if (
          typeof (this.state as any).universalWebAudioPlayer.clearQueue ===
          "function"
        ) {
          (this.state as any).universalWebAudioPlayer.clearQueue();
          console.log(`[AudioController] Universal播放器队列已清空`);
        }
        // 清空Universal播放器的addedIds（如果存在）
        if ((this.state as any).universalWebAudioPlayer.addedIds) {
          (this.state as any).universalWebAudioPlayer.addedIds.clear();
          console.log(`[AudioController] Universal播放器addedIds已清空`);
        }
      }

      // 重置当前索引
      this.state.currentIndex = 0;

      // 清理高性能队列相关数据
      if (this.state.highPerformanceQueue) {
        // 清空高性能队列（如果有clear方法）
        if (typeof this.state.highPerformanceQueue.clear === "function") {
          this.state.highPerformanceQueue.clear();
          console.log(`[AudioController] 高性能队列已清空`);
        }
      }

      console.log(`[AudioController] 所有播放队列及相关数据结构已清空`);
    } catch (error) {
      console.error(`[AudioController] 清空队列时出错:`, error);
    }
  }

  /**
   * 获取当前播放状态
   * @returns 是否正在播放
   */
  isPlaying(): boolean {
    return this.state.isPlaying || false;
  }

  /**
   * 设置产品切换相关状态
   * @param productId 目标产品ID
   */
  setProductSwitch(productId: string | null): void {
    try {
      // 设置产品切换状态
      this.state.switchingProductId = productId;
      this.state.lastProductSwitchTime = Date.now();

      // 为iOS WebAudio播放器设置允许的产品ID
      if (
        this.state.__appleWA__ &&
        this.state.__appleWA__.player &&
        typeof this.state.__appleWA__.player.setAllowedProductId === "function"
      ) {
        this.state.__appleWA__.player.setAllowedProductId(productId);
      }

      // 为Universal WebAudio播放器设置允许的产品ID
      if (
        (this.state as any).universalWebAudioPlayer &&
        typeof (this.state as any).universalWebAudioPlayer
          .setAllowedProductId === "function"
      ) {
        (this.state as any).universalWebAudioPlayer.setAllowedProductId(
          productId
        );
      }

      console.log(`[AudioController] 产品切换状态已设置:`, productId);
    } catch (error) {
      console.error(`[AudioController] 设置产品切换状态时出错:`, error);
    }
  }

  /**
   * 清空内部去重集合（如addedIds）
   * 专门用于清理内部数据结构，不影响播放状态
   */
  clearInternalCollections(): void {
    try {
      // 清理iOS WebAudio播放器的内部集合
      if (this.state.__appleWA__) {
        // 清空去重集合 addedIds
        if (this.state.__appleWA__.addedIds) {
          this.state.__appleWA__.addedIds.clear();
          console.log(`[AudioController] iOS播放器addedIds已清空`);
        }
        // 清空ID版本映射
        if (this.state.__appleWA__.idVersionMap) {
          this.state.__appleWA__.idVersionMap.clear();
          console.log(`[AudioController] iOS播放器idVersionMap已清空`);
        }
      }

      // 清理Universal WebAudio播放器的内部集合
      if ((this.state as any).universalWebAudioPlayer) {
        if ((this.state as any).universalWebAudioPlayer.addedIds) {
          (this.state as any).universalWebAudioPlayer.addedIds.clear();
          console.log(`[AudioController] Universal播放器addedIds已清空`);
        }
        // 检查并清理其他可能的集合类型
        if ((this.state as any).universalWebAudioPlayer.idVersionMap) {
          (this.state as any).universalWebAudioPlayer.idVersionMap.clear();
          console.log(`[AudioController] Universal播放器idVersionMap已清空`);
        }
      }

      // 清理高性能队列相关数据
      if (this.state.highPerformanceQueue) {
        if (typeof this.state.highPerformanceQueue.clear === "function") {
          this.state.highPerformanceQueue.clear();
          console.log(`[AudioController] 高性能队列已清空`);
        }
      }

      console.log(`[AudioController] 所有内部数据集合已清空`);
    } catch (error) {
      console.error(`[AudioController] 清空内部集合时出错:`, error);
    }
  }
}

/**
 * 创建音频控制器的工厂函数
 * @param state 当前房间状态
 * @param roomId 房间ID
 * @returns 音频控制器实例
 */
export function createAudioController(
  state: RoomState & { [key: string]: any },
  roomId: string
): IAudioController {
  return new AudioController(state, roomId);
}

/**
 * 从全局状态中获取房间状态并创建音频控制器
 * @param rootState 全局状态
 * @param roomId 房间ID
 * @returns 音频控制器实例或null
 */
export function createAudioControllerFromRoot(
  rootState: RootState & { [key: string]: any },
  roomId?: string
): IAudioController | null {
  const targetRoomId = roomId || rootState.currentRoomId;
  if (!targetRoomId || !rootState.roomStates.has(targetRoomId)) {
    console.error(`[AudioController] 无法创建控制器: 房间不存在`, targetRoomId);
    return null;
  }

  const roomState = rootState.roomStates.get(targetRoomId);
  return createAudioController(roomState, targetRoomId);
}
